import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";

const GrandCinemazTeam = () => {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchTeam = async () => {
    try {
      const res = await fetch(
        "https://apigulmohar.dodunsoftsolutions.com/api/team/company/GrandCinemaz"
      );
      const data = await res.json();
      setTeam(data);
    } catch (error) {
      console.error("Failed to fetch team:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeam();
  }, []);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 12,
      },
    },
  };

  return (
    <div
      className="py-12 px-4 md:px-8 lg:px-16 min-h-screen"
      style={{
        background: "linear-gradient(135deg, #0c1527 0%, #1a0505 100%)",
        backgroundAttachment: "fixed",
      }}
    >
      {/* Header with elegant cinema-inspired design */}
      <div className="max-w-7xl mx-auto mb-16 text-center">
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="relative inline-block"
        >
          <div className="absolute inset-0 blur-sm bg-red-700 opacity-30 transform -skew-x-12"></div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold relative z-10 text-transparent bg-clip-text bg-gradient-to-r from-red-700 via-red-600 to-blue-900">
            Meet Our Stellar Cast
          </h1>
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.8 }}
        >
          <p className="mt-4 text-lg md:text-xl max-w-3xl mx-auto text-gray-300 italic">
            The extraordinary team behind Grand Cinemaz's legendary experience
          </p>
          <div className="h-1 w-24 bg-gradient-to-r from-yellow-600 to-yellow-400 mx-auto mt-6 rounded-full"></div>
        </motion.div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="relative w-20 h-20">
            <div className="absolute top-0 left-0 w-full h-full border-8 border-gray-800 rounded-full"></div>
            <div className="absolute top-0 left-0 w-full h-full border-8 border-t-red-700 border-r-transparent border-b-transparent border-l-transparent rounded-full animate-spin"></div>
            <div className="absolute top-2 left-2 w-16 h-16 border-8 border-t-blue-900 border-r-transparent border-b-transparent border-l-transparent rounded-full animate-spin"></div>
          </div>
        </div>
      ) : team.length === 0 ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center text-xl text-gray-300 bg-gray-900 bg-opacity-50 p-8 rounded-lg max-w-2xl mx-auto border-l-4 border-red-800"
        >
          No team members found in our current production.
        </motion.div>
      ) : (
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="max-w-7xl mx-auto"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
            {team.map((member) => (
              <motion.div
                key={member._id}
                variants={itemVariants}
                whileHover={{ y: -10, transition: { duration: 0.3 } }}
                className="rounded-lg overflow-hidden group"
                style={{
                  background:
                    "linear-gradient(145deg, #0a1428 0%, #20080d 100%)",
                  boxShadow: "0 10px 30px rgba(0, 0, 0, 0.5)",
                }}
              >
                <div className="relative overflow-hidden">
                  {/* Gold accent border top */}
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-yellow-600 to-yellow-400 z-10"></div>

                  {/* Image with overlay */}
                  <div className="relative h-auto overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent opacity-60 z-10"></div>
                    <div className="absolute inset-0 bg-blue-900 mix-blend-color opacity-30 z-10 group-hover:opacity-0 transition-opacity duration-500"></div>
                    <img
                      src={member.imageUrl || "/api/placeholder/400/500"}
                      alt={member.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                  </div>

                  {/* Role badge */}
                  <div className="absolute bottom-4 right-4 z-20">
                    <span className="px-3 py-1 text-xs font-semibold bg-red-800 text-white rounded-full shadow-lg">
                      {member.role}
                    </span>
                  </div>
                </div>

                <div className="p-6 border-t border-gray-800">
                  <h2 className="text-2xl font-bold text-white mb-1">
                    {member.name}
                  </h2>
                  <p className="text-red-500 font-medium mb-3">
                    {member.title}
                  </p>

                  {member.description && (
                    <p className="text-gray-400 mt-3 leading-relaxed">
                      {member.description}
                    </p>
                  )}

                  {member.linkedIn && (
                    <div className="mt-5">
                      <a
                        href={member.linkedIn}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-4 py-2 rounded bg-gradient-to-r from-blue-900 to-red-900 text-white text-sm group-hover:from-red-800 group-hover:to-blue-800 transition-all duration-300"
                      >
                        <span>Connect on LinkedIn</span>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 ml-2"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 7l5 5m0 0l-5 5m5-5H6"
                          />
                        </svg>
                      </a>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Decorative elements */}
      <div className="absolute top-10 right-10 w-32 h-32 rounded-full bg-red-800 opacity-10 blur-3xl"></div>
      <div className="absolute bottom-10 left-10 w-40 h-40 rounded-full bg-blue-900 opacity-10 blur-3xl"></div>

      {/* Footer accent */}
      <div className="mt-20 max-w-4xl mx-auto">
        <div className="h-px bg-gradient-to-r from-transparent via-yellow-600 to-transparent"></div>
        <p className="text-center text-gray-500 mt-6 text-sm">
          Grand Cinemaz — Where Every Frame Tells a Story
        </p>
      </div>
    </div>
  );
};

export default GrandCinemazTeam;
